import React, {createContext, useContext, useMemo, useState} from "react";
import {ThemeProvider, createTheme, CssBaseline} from "@mui/material";
import {alpha} from "@mui/material/styles";

type ColorMode = "dark" | "light";

type ColorModeContextValue = {
    mode: ColorMode;
    toggleColorMode: () => void;
    setMode: (mode: ColorMode) => void;
};

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

export const useColorMode = () => {
    const ctx = useContext(ColorModeContext);
    if (!ctx) throw new Error("useColorMode must be used within ColorModeProvider");
    return ctx;
};

const STORAGE_KEY = "colorMode";

export const BRAND = {
    accent: "#0EC8D6",
    cyanDeep: "#08838E",
    navy: "#002564",
    dark: "#171729",
    header: "#0B2A3C",
    headerEnd: "#0F3A4F",
    headerDark: "#191a2a",
    headerDarkEnd: "#1e2033",
};

const getInitialMode = (): ColorMode => {
    const saved = localStorage.getItem(STORAGE_KEY) as ColorMode | null;
    if (saved === "light" || saved === "dark") return saved;
    return "light";
};

export const ColorModeProvider = ({children}: { children: React.ReactNode }) => {
    const [mode, setModeState] = useState<ColorMode>(getInitialMode);

    const setMode = (newMode: ColorMode) => {
        setModeState(newMode);
        localStorage.setItem(STORAGE_KEY, newMode);
    };

    const toggleColorMode = () => {
        setMode(mode === "light" ? "dark" : "light");
    };

    const theme = useMemo(
        () => {
            const isLight = mode === "light";

            return createTheme({
                palette: {
                    mode,
                    // BITLAB brand (bitlab.kz): accent #0EC8D6, accent-2 #002564, brand-dark #171729
                    // light: deeper cyan so white button text passes WCAG AA (4.5:1); dark: brand cyan with dark text
                    primary: isLight
                        ? {main: BRAND.cyanDeep, dark: "#076f78", light: BRAND.accent, contrastText: "#ffffff"}
                        : {main: BRAND.accent, dark: "#0a9aa6", light: "#5fdde6", contrastText: BRAND.header},

                    secondary: isLight
                        ? {main: BRAND.navy, dark: "#001a47", light: "#213a8f", contrastText: "#ffffff"}
                        : {main: "#7fb8ff", dark: "#4c93ff", light: "#a9cfff", contrastText: BRAND.dark},

                    background: {
                        default: isLight ? "#f5f7f8" : "#14141f",
                        paper: isLight ? "#ffffff" : "#1e2033",
                    },

                    text: isLight
                        ? {primary: BRAND.dark, secondary: "#5b6872"}
                        : {primary: "#eef1f7", secondary: "#a7acc7"},

                    divider: isLight ? "#dde3e7" : "#34364f",
                },

                // sx numeric radii are multiplied by this: borderRadius: 2 → 8px, 3 → 12px
                shape: {borderRadius: 4},

                typography: {
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    h1: {fontWeight: 800, letterSpacing: "-0.025em"},
                    h2: {fontWeight: 800, letterSpacing: "-0.025em"},
                    h3: {fontWeight: 700, letterSpacing: "-0.02em"},
                    h4: {fontWeight: 700, letterSpacing: "-0.02em"},
                    h5: {fontWeight: 700, letterSpacing: "-0.01em"},
                    h6: {fontWeight: 600, letterSpacing: "-0.01em"},
                    subtitle1: {fontWeight: 600},
                    subtitle2: {fontWeight: 600},
                    button: {fontWeight: 600},
                    overline: {fontWeight: 700, letterSpacing: "0.08em"},
                },

                components: {
                    MuiCssBaseline: {
                        styleOverrides: {
                            body: {
                                scrollbarColor: isLight ? "#c7ccd1 transparent" : "#34364f transparent",
                            },
                            "::selection": {
                                backgroundColor: alpha(BRAND.accent, isLight ? 0.25 : 0.35),
                            },
                        },
                    },

                    MuiButton: {
                        defaultProps: {
                            disableElevation: true,
                        },
                        styleOverrides: {
                            root: {
                                textTransform: "none",
                                borderRadius: 8,
                            },
                            containedPrimary: {
                                boxShadow: `0 8px 24px -12px ${alpha(BRAND.accent, 0.7)}`,
                                "&:hover": {
                                    backgroundColor: isLight ? "#076f78" : "#0a9aa6",
                                    boxShadow: `0 10px 28px -10px ${alpha(BRAND.accent, 0.8)}`,
                                },
                            },
                            outlined: ({theme}) => ({
                                borderColor:
                                    theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.28)"
                                        : "rgba(0,0,0,0.23)",
                                color: theme.palette.text.primary,
                                "&:hover": {
                                    borderColor:
                                        theme.palette.mode === "dark"
                                            ? "rgba(255,255,255,0.45)"
                                            : "rgba(0,0,0,0.35)",
                                    backgroundColor:
                                        theme.palette.mode === "dark"
                                            ? "rgba(255,255,255,0.06)"
                                            : "rgba(0,0,0,0.04)",
                                },
                            }),
                        },
                    },

                    MuiIconButton: {
                        styleOverrides: {
                            root: {borderRadius: 8},
                        },
                    },

                    MuiPaper: {
                        styleOverrides: {
                            root: ({theme}) => ({
                                border: `1px solid ${theme.palette.divider}`,
                                backgroundImage: "none",
                            }),
                            rounded: {
                                borderRadius: 14,
                            },
                        },
                    },

                    MuiMenu: {
                        styleOverrides: {
                            paper: {
                                borderRadius: 10,
                                boxShadow: isLight
                                    ? "0 10px 30px -10px rgba(15,23,42,0.18)"
                                    : "0 10px 30px -10px rgba(0,0,0,0.6)",
                            },
                        },
                    },

                    MuiPopover: {
                        styleOverrides: {
                            paper: {
                                borderRadius: 10,
                                boxShadow: isLight
                                    ? "0 10px 30px -10px rgba(15,23,42,0.18)"
                                    : "0 10px 30px -10px rgba(0,0,0,0.6)",
                            },
                        },
                    },

                    MuiDialog: {
                        styleOverrides: {
                            paper: {borderRadius: 22},
                        },
                    },

                    MuiOutlinedInput: {
                        styleOverrides: {
                            root: ({theme}) => ({
                                borderRadius: 8,
                                backgroundColor: theme.palette.background.paper,
                            }),
                            notchedOutline: {
                                borderColor: isLight ? "#c7ccd1" : "#3c3f5a",
                            },
                        },
                    },

                    MuiChip: {
                        styleOverrides: {
                            root: {fontWeight: 500},
                        },
                    },

                    MuiAlert: {
                        styleOverrides: {
                            root: {borderRadius: 10},
                        },
                    },

                    MuiTooltip: {
                        styleOverrides: {
                            tooltip: {
                                borderRadius: 6,
                                fontSize: 12,
                                backgroundColor: isLight ? BRAND.dark : "#262943",
                            },
                        },
                    },

                    MuiTableCell: {
                        styleOverrides: {
                            head: ({theme}) => ({
                                fontWeight: 600,
                                color: theme.palette.text.secondary,
                            }),
                        },
                    },

                    MuiListItemButton: {
                        styleOverrides: {
                            root: ({theme}) => ({
                                borderRadius: 8,
                                "&.Mui-selected": {
                                    backgroundColor: alpha(theme.palette.primary.main, isLight ? 0.1 : 0.16),
                                    color: theme.palette.primary.main,
                                    "&:hover": {
                                        backgroundColor: alpha(theme.palette.primary.main, isLight ? 0.12 : 0.22),
                                    },
                                },
                            }),
                        },
                    },
                },
            });
        },
        [mode]
    );

    const value = useMemo(() => ({mode, toggleColorMode, setMode}), [mode]);

    return (
        <ColorModeContext.Provider value={value}>
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                {children}
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};
